import { Modal } from 'components/atoms/Modal';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { IconTenant } from 'components/atoms/icons/IconTenant';
import { WasteUnitItem } from 'types/routeManagementTypes';
import { getUnitLabel, normalizeNumber } from 'util/commons';
import BigNumber from 'bignumber.js';
import { IconFixedItem } from 'components/atoms/icons/IconFixedItem';
import Popover from 'components/atoms/Popover';

interface TenantRegistrationConfirmModalProps {
  onClose: () => void;
  registeredItems: WasteUnitItem[];
  totalRegisteredWeight: number;
  handleSubmit: () => void;
  tenantName?: string;
}

export const TenantRegistrationConfirmModal = ({
  onClose,
  registeredItems,
  totalRegisteredWeight,
  tenantName,
  handleSubmit,
}: TenantRegistrationConfirmModalProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.customModal}>
      <Modal>
        <div className={styles.header}>
          <div className={styles.title}>
            <h3>{t('tenant_registration.modal_confirm.title')}</h3>
            <span className={styles.note}>
              {t('tenant_registration.modal_confirm.note')}
            </span>
          </div>

          {tenantName && (
            <div className={styles.tenantNameWrapper}>
              <span className={styles.destinationLabel}>
                {t('tenant_registration.modal_confirm.destination_tenant')}
              </span>
              <div className={styles.tenantName}>
                <IconTenant />
                <Popover
                  content={tenantName}
                  autoGen={{
                    odd: 10,
                    wrapperStyle: {
                      width: 'fit-content',
                      wordBreak: 'break-all',
                    },
                  }}
                  containerStyle={{
                    zIndex: '30001',
                  }}
                  positions={['top']}
                >
                  <span>{tenantName}</span>
                </Popover>
              </div>
            </div>
          )}
        </div>
        <div className={styles.content}>
          <div className={styles.itemListWrapper}>
            <div className={styles.itemList}>
              {registeredItems.map((item) => (
                <div key={item.id} className={styles.item}>
                  <div>
                    <div className={styles.itemName}>
                      <span>{item.name}</span>
                      {item.type === 'Fixed' && (
                        <IconFixedItem color="#86ea5c" />
                      )}
                    </div>
                    {item.type === 'Fixed' && (
                      <div className={styles.itemConvertWeight}>
                        {`${item.convertKg}kg/${item.quantity}${getUnitLabel(
                          item.unit
                        )}`}
                      </div>
                    )}
                  </div>
                  <div>
                    <span className={styles.itemWeight}>
                      {normalizeNumber({
                        value: BigNumber(item.weight || 0)
                          .multipliedBy(item.convertKg || 1)
                          .toNumber(),
                      })}
                    </span>
                    <span className={styles.unit}>kg</span>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.totalWeight}>
              <span className={styles.totalWeightLabel}>
                {t('tenant_registration.modal_confirm.total_weight')}
              </span>
              <div>
                <span className={styles.totalWeightValue}>
                  {normalizeNumber({
                    value: totalRegisteredWeight,
                  })}
                </span>
                <span className={styles.unit}>kg</span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <button className={styles.editButton} onClick={onClose}>
            {t('common.button.cancel')}
          </button>
          <button className={styles.registerButton} onClick={handleSubmit}>
            {t('tenant_registration.modal_confirm.register_btn')}
          </button>
        </div>
      </Modal>
    </div>
  );
};
